// 搜索结果的筛选与排序（纯函数，无 UI 依赖）。
//
// 全部在客户端做：实测 Duffel 默认搜索返回的结果里已包含全部直飞报价
// （PVG→LAX 229 条中的 4 条直飞，与专门用 max_connections=0 搜到的完全一致），
// 所以没必要为改条件重新查一次——那样既慢又要多付一次查询费。
import { localHour, describeFareCondition } from './flightData';

export const TIME_BUCKETS = [
  { key: 'dawn', label: '凌晨', hint: '00:00-06:00', from: 0, to: 6 },
  { key: 'morning', label: '上午', hint: '06:00-12:00', from: 6, to: 12 },
  { key: 'afternoon', label: '下午', hint: '12:00-18:00', from: 12, to: 18 },
  { key: 'evening', label: '晚上', hint: '18:00-24:00', from: 18, to: 24 },
];

export const TRIP_TYPES = [
  { key: 'ow', label: '单程' },
  { key: 'rt', label: '往返' },
  { key: 'mc', label: '多程' },
];

// 与后端 flight/models.py 的 MAX_LEGS 保持一致
export const MAX_LEGS = 5;

export const STOP_OPTIONS = [
  { key: 'any', label: '不限' },
  { key: 'direct', label: '直飞' },
  { key: 'max1', label: '最多1次中转' },
];

export const SORT_OPTIONS = [
  { key: 'price', label: '价格最低' },
  { key: 'duration', label: '耗时最短' },
  { key: 'departure', label: '起飞最早' },
];

export const DEFAULT_FILTERS = {
  stops: 'any',
  depBuckets: [],
  arrBuckets: [],
  airlines: [],
  maxDurationMin: null,
  refundFree: false,
  changeFree: false,
};

// 「免费」只认 level === 'free'。条件未知（航司没给信息，约占 40%）绝不能算进来，
// 否则勾了「可免费退票」却给出一堆退改不明的票，比不做还糟。
export const isFreeRefund = (offer) => describeFareCondition(offer.refund, 'refund').level === 'free';
export const isFreeChange = (offer) => describeFareCondition(offer.change, 'change').level === 'free';

export const totalDuration = (offer) =>
  (offer.segments || []).reduce((sum, s) => sum + (s.duration_minutes || 0), 0);

// 统一取 slices：老数据（下过的订单快照）只有扁平 segments，兜底包成单个 slice
export const offerSlices = (offer) =>
  (offer.slices && offer.slices.length)
    ? offer.slices
    : [{
      origin: (offer.segments || [])[0]?.dep_airport,
      destination: (offer.segments || [])[(offer.segments || []).length - 1]?.arr_airport,
      segments: offer.segments || [],
    }];

// 表单条件 → 航段列表，与后端 FlightSearchRequest.as_legs() 保持一致
export const tripLegs = (c) => {
  if (c.trip_type === 'mc') return c.legs || [];
  const first = { origin: c.origin, destination: c.destination, depart_date: c.depart_date };
  if (c.trip_type === 'rt') {
    return [first, { origin: c.destination, destination: c.origin, depart_date: c.return_date }];
  }
  return [first];
};

export const totalPrice = (offer) =>
  offer.total_amount ?? ((offer.price_per_adult || 0) + (offer.tax_fee || 0));

const inBuckets = (iso, bucketKeys) => {
  if (!bucketKeys.length) return true;
  const h = localHour(iso);
  if (h === null) return true; // 时刻缺失不参与过滤，避免误杀
  return bucketKeys.some((k) => {
    const b = TIME_BUCKETS.find((x) => x.key === k);
    return b && h >= b.from && h < b.to;
  });
};

export const isFiltered = (f) =>
  f.stops !== 'any' || f.depBuckets.length > 0 || f.arrBuckets.length > 0 ||
  f.airlines.length > 0 || f.maxDurationMin !== null || f.refundFree || f.changeFree;

/** 从「未筛选」的全量结果算出可选项，勾选后选项不会自己消失。 */
export function deriveFacets(offers) {
  const byAirline = new Map();
  let minDur = Infinity;
  let maxDur = 0;

  offers.forEach((o) => {
    const first = (o.segments || [])[0];
    if (first) {
      const cur = byAirline.get(first.airline_code);
      if (cur) cur.count += 1;
      else byAirline.set(first.airline_code, {
        code: first.airline_code, name: first.airline_name_zh, count: 1,
      });
    }
    const d = totalDuration(o);
    if (d > 0) {
      if (d < minDur) minDur = d;
      if (d > maxDur) maxDur = d;
    }
  });

  return {
    airlines: [...byAirline.values()].sort((a, b) => b.count - a.count || a.code.localeCompare(b.code)),
    durationRange: offers.length && maxDur > 0 ? [minDur, maxDur] : null,
    directCount: offers.filter(
      (o) => offerSlices(o).every((s) => (s.segments || []).length === 1),
    ).length,
    freeRefundCount: offers.filter(isFreeRefund).length,
    freeChangeCount: offers.filter(isFreeChange).length,
  };
}

export function applyFilters(offers, filters) {
  const f = { ...DEFAULT_FILTERS, ...filters };
  return offers.filter((o) => {
    const segs = o.segments || [];
    if (!segs.length) return false;
    const slices = offerSlices(o);

    // 中转次数按「每一程都要满足」判定：去程直飞但回程转两次，不能算直飞
    const maxSegs = Math.max(...slices.map((s) => (s.segments || []).length));
    if (f.stops === 'direct' && maxSegs !== 1) return false;
    if (f.stops === 'max1' && maxSegs > 2) return false;

    // 时间筛选针对第一程（去程）：往返票里用户关心的是什么时候出发、什么时候到目的地
    const out = slices[0].segments || [];
    if (!inBuckets(out[0]?.dep_time, f.depBuckets)) return false;
    if (!inBuckets(out[out.length - 1]?.arr_time, f.arrBuckets)) return false;

    if (f.airlines.length && !f.airlines.includes(segs[0].airline_code)) return false;

    if (f.maxDurationMin !== null && totalDuration(o) > f.maxDurationMin) return false;

    if (f.refundFree && !isFreeRefund(o)) return false;
    if (f.changeFree && !isFreeChange(o)) return false;

    return true;
  });
}

export function sortOffers(offers, key) {
  const list = [...offers];
  if (key === 'duration') {
    list.sort((a, b) => totalDuration(a) - totalDuration(b) || totalPrice(a) - totalPrice(b));
  } else if (key === 'departure') {
    const dep = (o) => (o.segments || [])[0]?.dep_time || '';
    list.sort((a, b) => dep(a).localeCompare(dep(b)) || totalPrice(a) - totalPrice(b));
  } else {
    list.sort((a, b) => totalPrice(a) - totalPrice(b));
  }
  return list;
}
